import { theme } from '../../theme';
import { fontWeights } from '../../fontWeights';
const colors = {
    main: '#1c887b',
    highlight: '#23c2af',
};
const font = '-apple-system, BlinkMacSystemFont, avenir next, avenir, segoe ui, helvetica neue, helvetica, Cantarell, Ubuntu, roboto, noto, arial, sans-serif';
/**
 * White-label brand
 */
export const whiteLabelBrand = {
    core: {
        color: {
            background: theme.color.coal02,
            content: theme.color.coal,
            outline: {
                positive: {
                    outer: theme.color.coal,
                    inner: theme.color.white,
                },
                negative: {
                    outer: theme.color.white,
                    inner: theme.color.coal,
                },
            },
        },
        font: {
            l: {
                family: font,
                lineHeight: theme.typography.lineHeight.body.l,
                letterSpacing: theme.typography.letterSpacing.body.l,
                size: theme.typography.fontSize.body.l,
                weight: fontWeights.normal.toString(),
            },
            m: {
                family: font,
                lineHeight: theme.typography.lineHeight.body.m,
                letterSpacing: theme.typography.letterSpacing.body.m,
                size: theme.typography.fontSize.body.m,
                weight: fontWeights.normal.toString(),
            },
        },
        border: {
            xl: {
                radius: theme.border.radius.full,
            },
            l: {
                radius: theme.border.radius.l,
            },
            m: {
                radius: theme.border.radius.m,
            },
            s: {
                radius: theme.border.radius.s,
            },
            xs: {
                radius: theme.border.radius.xs,
            },
        },
    },
    interaction: {
        color: {
            default: {
                background: theme.color.white,
                content: colors.main,
                border: theme.color.coal15,
                shadow: `${theme.color.coal}0D`,
                shadowLarge: theme.color.coal04,
                overlay: theme.color.coal,
            },
            highlight: {
                background: theme.color.coal02,
                content: colors.highlight,
            },
            muted: {
                content: theme.color.coal22,
            },
        },
    },
    forms: {
        color: {
            default: {
                background: {
                    onLight: theme.color.white,
                    onDark: theme.color.coal04,
                },
                content: theme.color.spruce,
                border: colors.main,
            },
            selected: {
                border: colors.highlight,
                content: theme.color.white,
                background: colors.highlight,
            },
            muted: {
                border: theme.color.coal65,
                background: theme.color.coal15,
                content: theme.color.coal65,
            },
            success: {
                content: theme.color.okayGreen,
            },
            error: {
                background: theme.color.lingonberryPorridge15,
                content: theme.color.heather,
            },
            warning: {
                background: theme.color.cloudberry15,
                content: theme.color.cloudberry150,
                border: theme.color.cloudberry,
            },
            invalid: {
                background: theme.color.lingonberryPorridge15,
                content: theme.color.lingonberryPorridge150,
            },
        },
    },
    cta: {
        font: {
            family: font,
            letterSpacing: theme.typography.letterSpacing.button.m,
            lineHeight: theme.typography.lineHeight.button.m,
            size: theme.typography.fontSize.button.m,
            weight: fontWeights.bold.toString(),
        },
        color: {
            primary: {
                positive: {
                    default: {
                        background: colors.main,
                        content: theme.color.white,
                        border: colors.main,
                    },
                    highlight: {
                        background: colors.highlight,
                        content: theme.color.white,
                        border: colors.highlight,
                    },
                    muted: {
                        background: theme.color.coal65,
                        content: theme.color.white,
                        border: theme.color.coal65,
                    },
                },
                negative: {
                    default: {
                        background: theme.color.white,
                        content: colors.main,
                        border: theme.color.white,
                    },
                    highlight: {
                        background: theme.color.rainyBlue15,
                        content: colors.highlight,
                        border: theme.color.white,
                    },
                    muted: {
                        background: theme.color.white,
                        content: theme.color.coal65,
                        border: theme.color.coal65,
                    },
                },
            },
            secondary: {
                positive: {
                    default: {
                        background: theme.color.white,
                        content: colors.main,
                        border: colors.main,
                    },
                    highlight: {
                        background: theme.color.rainyBlue15,
                        content: colors.highlight,
                        border: colors.highlight,
                    },
                    muted: {
                        background: theme.color.coal02,
                        content: theme.color.coal65,
                        border: theme.color.coal22,
                    },
                },
                negative: {
                    default: {
                        background: 'transparent',
                        content: theme.color.white,
                        border: theme.color.white,
                    },
                    highlight: {
                        background: colors.highlight,
                        content: theme.color.white,
                        border: 'transparent',
                    },
                    muted: {
                        background: 'transparent',
                        content: theme.color.coal22,
                        border: theme.color.coal15,
                    },
                },
            },
            link: {
                default: {
                    content: colors.main,
                },
                highlight: {
                    content: colors.highlight,
                },
                visited: {
                    content: theme.color.heather,
                },
            },
        },
    },
    navigation: {
        color: {
            default: {
                background: colors.main,
                content: {
                    onDark: theme.color.white,
                    onLight: theme.color.coal04,
                },
                border: colors.main,
                shadow: `${theme.color.coal}1A`,
            },
            highlight: {
                content: {
                    onDark: colors.main,
                    onLight: colors.highlight,
                },
                background: colors.main,
            },
        },
    },
    feedback: {
        color: {
            warning: {
                background: theme.color.cloudberry15,
                content: theme.color.cloudberry150,
                border: theme.color.cloudberry,
            },
            error: {
                background: theme.color.lingonberryPorridge15,
                content: theme.color.lingonberryPorridge150,
                border: theme.color.lingonberryPorridge,
            },
            info: {
                background: theme.color.rainyBlue40,
                content: colors.main,
                border: theme.color.rainyBlue,
            },
            success: {
                background: theme.color.spruce15,
                content: theme.color.spruce150,
                border: theme.color.okayGreen,
            },
            loading: {
                background: theme.color.coal15,
                content: `${theme.color.coal04}BF`,
            },
            veryNegative: theme.color.lingonberryPorridge,
            negative: theme.color.lingonberryPorridge40,
            neutral: {
                background: theme.color.coal04,
                content: theme.color.coal65,
            },
            positive: theme.color.spruce15,
            veryPositive: theme.color.spruce40,
        },
    },
    heading: {
        color: {
            content: colors.main,
        },
        font: {
            xxxl: {
                family: font,
                lineHeight: theme.typography.lineHeight.title.xxxl,
                size: theme.typography.fontSize.title.xxxl,
                letterSpacing: theme.typography.letterSpacing.title.xxxl,
                weight: fontWeights.black.toString(),
            },
            xxl: {
                family: font,
                lineHeight: theme.typography.lineHeight.title.xxl,
                size: theme.typography.fontSize.title.xxl,
                letterSpacing: theme.typography.letterSpacing.title.xxl,
                weight: fontWeights.black.toString(),
            },
            xl: {
                family: font,
                lineHeight: theme.typography.lineHeight.title.xl,
                size: theme.typography.fontSize.title.xl,
                letterSpacing: theme.typography.letterSpacing.title.xl,
                weight: fontWeights.black.toString(),
            },
            l: {
                family: font,
                lineHeight: theme.typography.lineHeight.title.l,
                size: theme.typography.fontSize.title.l,
                letterSpacing: theme.typography.letterSpacing.title.l,
                weight: fontWeights.black.toString(),
            },
        },
    },
    subHeading: {
        color: {
            content: theme.color.coal,
        },
        font: {
            xxl: {
                family: font,
                lineHeight: theme.typography.lineHeight.subtitle.xxl,
                size: theme.typography.fontSize.subtitle.xxl,
                letterSpacing: theme.typography.letterSpacing.subtitle.xxl,
                weight: fontWeights.bold.toString(),
            },
            xl: {
                family: font,
                lineHeight: theme.typography.lineHeight.subtitle.xl,
                size: theme.typography.fontSize.subtitle.xl,
                letterSpacing: theme.typography.letterSpacing.subtitle.xl,
                weight: fontWeights.bold.toString(),
            },
            l: {
                family: font,
                lineHeight: theme.typography.lineHeight.subtitle.l,
                size: theme.typography.fontSize.subtitle.l,
                letterSpacing: theme.typography.letterSpacing.subtitle.l,
                weight: fontWeights.bold.toString(),
            },
            mplus: {
                family: font,
                lineHeight: theme.typography.lineHeight.subtitle.mplus,
                size: theme.typography.fontSize.subtitle.mplus,
                letterSpacing: theme.typography.letterSpacing.subtitle.mplus,
                weight: fontWeights.bold.toString(),
            },
            m: {
                family: font,
                lineHeight: theme.typography.lineHeight.subtitle.m,
                size: theme.typography.fontSize.subtitle.m,
                letterSpacing: theme.typography.letterSpacing.subtitle.m,
                weight: fontWeights.bold.toString(),
            },
            s: {
                family: font,
                lineHeight: theme.typography.lineHeight.subtitle.s,
                size: theme.typography.fontSize.subtitle.s,
                letterSpacing: theme.typography.letterSpacing.subtitle.s,
                weight: fontWeights.bold.toString(),
            },
            xs: {
                family: font,
                lineHeight: theme.typography.lineHeight.subtitle.xs,
                size: theme.typography.fontSize.subtitle.xs,
                letterSpacing: theme.typography.letterSpacing.subtitle.xs,
                weight: fontWeights.bold.toString(),
            },
        },
    },
    lead: {
        color: {
            content: theme.color.coal,
        },
        font: {
            m: {
                family: font,
                lineHeight: theme.typography.lineHeight.lead.m,
                size: theme.typography.fontSize.lead.m,
                letterSpacing: theme.typography.letterSpacing.lead.m,
                weight: fontWeights.light.toString(),
            },
            s: {
                family: font,
                lineHeight: theme.typography.lineHeight.lead.s,
                size: theme.typography.fontSize.lead.s,
                letterSpacing: theme.typography.letterSpacing.lead.s,
                weight: fontWeights.light.toString(),
            },
        },
    },
    list: {
        font: {
            item: {
                family: font,
                lineHeight: theme.typography.lineHeight.list.m,
                size: theme.typography.fontSize.list.m,
                letterSpacing: theme.typography.letterSpacing.list.m,
                weight: fontWeights.light.toString(),
            },
            marker: {
                family: font,
                lineHeight: theme.typography.lineHeight.listNumber.m,
                size: theme.typography.fontSize.listNumber.m,
                letterSpacing: theme.typography.letterSpacing.listNumber.m,
                weight: fontWeights.black.toString(),
            },
        },
        color: {
            default: {
                border: `${theme.color.coal}1A`,
                background: {
                    onLight: theme.color.coal04,
                    onDark: theme.color.white,
                },
                item: {
                    content: theme.color.coal,
                    border: theme.color.coal04,
                },
                marker: {
                    content: colors.main,
                },
            },
            muted: {
                content: theme.color.coal65,
            },
        },
    },
};
